import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';
import { useLanguage } from '../../config/language';
import { useTheme } from '../../config/theme';
import { useSearchBox } from '../../hooks/useSearchBox';
import { SearchPanel } from './SearchPanel';

/** Full-screen search (mobile + tablet). */
export const SearchOverlay = ({ open, onClose }) => {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const box = useSearchBox({ language, onDone: onClose });
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    box.setQ('');
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { clearTimeout(t); document.body.style.overflow = prev; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className={`fixed inset-0 z-[10000] flex flex-col animate-fadeIn ${isDarkMode ? 'bg-[#0A0B10]/95' : 'bg-[#F8FAFC]/95'} backdrop-blur-xl`}
      dir={isAr ? 'rtl' : 'ltr'} role="dialog" aria-modal="true" data-testid="search-overlay">
      <div className={`flex items-center gap-2 px-4 h-16 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
        <Search className="h-5 w-5 text-[#1E9BD9] shrink-0" />
        <input
          ref={inputRef}
          value={box.q}
          onChange={(e) => box.setQ(e.target.value)}
          onKeyDown={box.onKeyDown}
          enterKeyHint="search"
          maxLength={200}
          placeholder={isAr ? 'ابحث عن رقم، لوحة، سيارة، عقار، خدمة…' : 'Search numbers, plates, cars, property, services…'}
          className={`flex-1 h-11 bg-transparent border-0 p-0 shadow-none outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 text-base ${isDarkMode ? 'text-white placeholder:text-slate-500' : 'text-slate-900 placeholder:text-slate-400'}`}
          data-testid="search-overlay-input"
        />
        <button type="button" onClick={onClose} aria-label={isAr ? 'إغلاق' : 'Close'} data-testid="search-overlay-close"
          className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}>
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-8">
        <SearchPanel box={box} isAr={isAr} isDarkMode={isDarkMode} />
      </div>
    </div>,
    document.body
  );
};

/** Icon trigger for the mobile header. */
export const MobileSearchButton = ({ className = '' }) => {
  const [open, setOpen] = useState(false);
  const { language } = useLanguage();
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label={language === 'ar' ? 'بحث' : 'Search'} data-testid="mobile-search-btn" className={className}>
        <Search className="h-5 w-5" />
      </button>
      <SearchOverlay open={open} onClose={() => setOpen(false)} />
    </>
  );
};
